import { memo } from "react";
import type { StyleProp, ViewStyle } from "react-native";

import { Box } from "@/components/atoms/Box";
import { FlashList, type ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import type { UserThread } from "@/generated/graphql";

/** Constant style object for FlashList content container */
const FLASH_LIST_CONTENT_STYLE: StyleProp<ViewStyle> = {
  paddingTop: 8,
  paddingBottom: 16,
};

/**
 * Props for ThreadListContentView component
 */
interface ThreadListContentViewProps {
  isLoading: boolean;
  errorMessage: string | null;
  emptyStateMessage: string | null;
  threads: UserThread[];
  keyExtractor: (item: UserThread) => string;
  renderItem: ListRenderItem<UserThread>;
}

/**
 * Shared content for thread list (loading, error, empty, or list)
 * @param root0 - Component props
 * @param root0.isLoading - Whether threads are loading
 * @param root0.errorMessage - Error message to display
 * @param root0.emptyStateMessage - Empty state message
 * @param root0.threads - List of threads
 * @param root0.keyExtractor - Pre-memoized key extractor function
 * @param root0.renderItem - Pre-memoized render item function
 */
const ThreadListContentView = ({
  isLoading,
  errorMessage,
  emptyStateMessage,
  threads,
  keyExtractor,
  renderItem,
}: ThreadListContentViewProps) => (
  <Box className="flex-1">
    {isLoading ? (
      <Box className="flex items-center justify-center gap-3 py-12">
        <Spinner size="small" className="text-needs-semantic-primary-600" />
        <Text variant="body-strong" className="text-content-muted">
          Loading conversations...
        </Text>
      </Box>
    ) : errorMessage ? (
      <Box className="flex flex-col items-center justify-center px-4 py-12">
        <Text
          variant="body-strong"
          className="text-center text-needs-semantic-error-600"
        >
          {errorMessage}
        </Text>
      </Box>
    ) : threads.length === 0 ? (
      <Box className="flex items-center justify-center px-4 py-12">
        <Text
          variant="body-strong"
          className="text-center leading-relaxed text-content-muted"
        >
          {emptyStateMessage}
        </Text>
      </Box>
    ) : (
      <FlashList
        data={threads}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        className="flex-1"
        contentContainerStyle={FLASH_LIST_CONTENT_STYLE}
        showsVerticalScrollIndicator={false}
      />
    )}
  </Box>
);

ThreadListContentView.displayName = "ThreadListContentView";

export default memo(ThreadListContentView);
